'use client'

const REQUEST_PROOF_HEADER = 'X-TLS-Request-Proof'
const REQUEST_PROOF_CHALLENGE_HEADER = 'X-TLS-Request-Challenge'
const REQUEST_PROOF_CACHE_MS = 10 * 60 * 1000

let cachedProof: string | null = null
let cachedAt = 0
let inFlightProofRequest: Promise<string> | null = null

async function fetchChallenge(): Promise<string> {
  const response = await fetch('/api/challenge', {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin',
  })

  const body = await response.json().catch(() => null)
  const challenge = body?.challenge

  if (!response.ok || typeof challenge !== 'string' || challenge.length === 0) {
    throw new Error(body?.error || 'Unable to obtain request challenge')
  }

  return challenge
}

async function fetchRequestProof(): Promise<string> {
  const challenge = await fetchChallenge()

  const response = await fetch('/api/request-proof', {
    method: 'GET',
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      [REQUEST_PROOF_CHALLENGE_HEADER]: challenge,
    },
  })

  const body = await response.json().catch(() => null)
  const proof = body?.proof

  if (!response.ok || typeof proof !== 'string' || proof.length === 0) {
    throw new Error(body?.error || 'Unable to verify request identity')
  }

  cachedProof = proof
  cachedAt = Date.now()
  return proof
}

export async function getRequestProof(forceRefresh = false): Promise<string> {
  const isCacheFresh = cachedProof && Date.now() - cachedAt < REQUEST_PROOF_CACHE_MS
  if (!forceRefresh && isCacheFresh && cachedProof) {
    return cachedProof
  }

  if (!inFlightProofRequest) {
    inFlightProofRequest = fetchRequestProof().finally(() => {
      inFlightProofRequest = null
    })
  }

  return inFlightProofRequest
}

export async function withRequestProofHeaders(headers?: HeadersInit): Promise<Headers> {
  const nextHeaders = new Headers(headers)
  nextHeaders.set(REQUEST_PROOF_HEADER, await getRequestProof())
  return nextHeaders
}
